import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import calendarNoConferencing from '../../../../assets/images/calendarNoConferencing.svg';
import {conferenceTypeToIcon, conferenceTypeToText} from '../../../../helpers/models/conferenceHelpers';
import {ConferenceTypesEnum} from '../../../../models/api/conferenceModel';
import {DropdownEmpty} from '../../../common/dropdowns/dropdownEmpty';
import {Pill} from '../../../pill/pill';

/*
 * Props.
 */

interface ConferenceDropdownBoxProps {
  conferenceType: ConferenceTypesEnum | undefined;
  availableConferenceTypes: ReadonlyArray<ConferenceTypesEnum>;
  isGlobalAdmin: boolean;
  defaultConferencingType: ConferenceTypesEnum | undefined;
  onConferenceTypeSelect: HandlerOf<ConferenceTypesEnum | undefined>;
  onGoToIntegrationSettingsClick: Handler;
  hasChangedConference?: boolean;
  className?: string;
  isEditing: boolean;
}

interface DropdownItemOptions {
  isDropdownValue?: boolean;
  isDefaultConferenceType?: boolean;
}

/*
 * Styles.
 */

const StyledEmptyContentDiv = styled.div`
  display: grid;
  padding: 0px 20px;
  justify-items: center;
  text-align: center;
  line-height: 24px;
  width: 268px;
`;

const StyledSettingsButtonWrapperDiv = styled.div`
  margin-top: 10px;
`;

interface ItemContentProps {
  $isValue?: boolean;
}

const StyledItemContentDiv = styled.div<ItemContentProps>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto;
  grid-column-gap: ${(p) => (p.$isValue ? 8 : 10)}px;
  justify-content: left;
  align-items: center;
`;

const StyledDefautPill = styled(Pill)`
  margin-left: -6px;
`;

/*
 * Component.
 */

export const ConferenceDropdownBox: FC<ConferenceDropdownBoxProps> = (props) => {
  useInitialEffect(() => {
    if (props.defaultConferencingType && !props.hasChangedConference && !props.isEditing) {
      props.onConferenceTypeSelect(props.defaultConferencingType);
    }
  });
  return (
    <DropdownBoxCoordinator
      value={renderItem(props.conferenceType, {isDropdownValue: true})}
      renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
      boxClassName={props.className}
      shouldDisablePadding
    />
  );
};
function renderDropdown(popoverProps: PopoverTransferProps, props: ConferenceDropdownBoxProps) {
  return <Dropdown {...popoverProps}>{renderDropdownContent(popoverProps, props)}</Dropdown>;
}

function renderDropdownContent(popoverProps: PopoverTransferProps, props: ConferenceDropdownBoxProps) {
  if (props.availableConferenceTypes.length === 0) {
    return (
      <DropdownEmpty imageSource={calendarNoConferencing}>{renderNoConferencingText(props)}</DropdownEmpty>
    );
  }

  return (
    <DropdownList {...popoverProps}>
      <DropdownItem
        onSelect={() => props.onConferenceTypeSelect(undefined)}
        isChecked={isUndefined(props.conferenceType)}
        shouldReserveRightIcon
      >
        <DropdownItemContent>{renderItem(undefined, {})}</DropdownItemContent>
      </DropdownItem>
      {props.availableConferenceTypes.map((t) => (
        <DropdownItem
          key={t}
          onSelect={() => props.onConferenceTypeSelect(t)}
          isChecked={t === props.conferenceType}
          shouldReserveRightIcon
        >
          <DropdownItemContent>
            {renderItem(t, {isDefaultConferenceType: t === props.defaultConferencingType})}
          </DropdownItemContent>
        </DropdownItem>
      ))}
    </DropdownList>
  );
}

function renderNoConferencingText(props: ConferenceDropdownBoxProps) {
  if (props.isGlobalAdmin) {
    return (
      <StyledEmptyContentDiv>
        {/* TODO: Update this prompt to not be Zoom-specific when we have other conferencing integrations. */}
        <Trans id="no-conferencing-admin-prompt">
          Enable the Zoom integration in settings to add conferencing to this meeting.
        </Trans>
        <StyledSettingsButtonWrapperDiv>
          <Button variant={ButtonVariantsEnum.LINK} onClick={props.onGoToIntegrationSettingsClick}>
            <Trans id="calendar-auth-settings">Go to Settings</Trans>
          </Button>
        </StyledSettingsButtonWrapperDiv>
      </StyledEmptyContentDiv>
    );
  }

  return (
    <StyledEmptyContentDiv>
      <Trans id="no-conferencing-prompt">
        No conferencing integrations enabled. Ask your administrator to enable a conferencing integration in
        Settings.
      </Trans>
    </StyledEmptyContentDiv>
  );
}

function renderItem(conferenceType: ConferenceTypesEnum | undefined, options: DropdownItemOptions) {
  const {isDropdownValue, isDefaultConferenceType} = options;

  if (!conferenceType) {
    const noneSize = isDropdownValue ? 'noneSmall' : 'none';
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledItemContentDiv $isValue={isDropdownValue}>
            <Icon name={noneSize} color={theme.greys.shade70} />
            <Trans id="conference-dropdown-no-conference">No video conference</Trans>
          </StyledItemContentDiv>
        )}
      </UserThemeConsumer>
    );
  }

  const maybeRenderDefaultPill = () => {
    if (isDropdownValue || !isDefaultConferenceType) {
      return null;
    }

    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledDefautPill
            size={VisualSizesEnum.MEDIUM}
            backgroundColor={theme.greys.shade30}
            color={theme.greys.shade80}
          >
            <Trans id="conference-dropdown-default-pill">Default</Trans>
          </StyledDefautPill>
        )}
      </UserThemeConsumer>
    );
  };

  return (
    <StyledItemContentDiv $isValue={isDropdownValue}>
      {conferenceTypeToIcon[conferenceType]}
      {conferenceTypeToText[conferenceType]}
      {maybeRenderDefaultPill()}
    </StyledItemContentDiv>
  );
}
